<template>
  <div class="live">
    <header class="live__masthead">
      <div class="live__mastheadtext">
        <div class="live__eyebrow">
          <span class="live__dot" :class="{ 'live__dot--on': data.total > 0 }"></span>
          Tiempo real
        </div>
        <h1 class="live__title">En vivo</h1>
        <p class="live__sub">{{ mode === 'chat' ? 'Conversaciones con visitantes en tiempo real.' : 'Quién está navegando el sitio ahora mismo y qué está viendo.' }}</p>
      </div>
      <div class="live__modes" role="tablist" aria-label="Vista">
        <button type="button" class="live__mode" role="tab" :aria-selected="String(mode === 'traffic')" :class="{ 'live__mode--on': mode === 'traffic' }" @click="mode = 'traffic'">Tráfico</button>
        <button type="button" class="live__mode" role="tab" :aria-selected="String(mode === 'chat')" :class="{ 'live__mode--on': mode === 'chat' }" @click="mode = 'chat'">Chat</button>
      </div>
    </header>

    <chat-inbox v-show="mode === 'chat'" ref="inbox" @open-property="$refs.detail.open($event)"></chat-inbox>

    <template v-if="mode === 'traffic'">

    <div class="live__kpis">
      <div class="live__kpi live__kpi--hero">
        <div class="live__kpi-num">{{ data.total || 0 }}</div>
        <div class="live__kpi-lbl">En línea ahora</div>
        <div v-if="data.away" class="live__kpi-extra">+{{ data.away }} en otra pestaña</div>
      </div>
      <div class="live__kpi">
        <div class="live__kpi-num">{{ data.onProperty || 0 }}</div>
        <div class="live__kpi-lbl">Viendo propiedades</div>
      </div>
      <div class="live__kpi">
        <div class="live__kpi-num">{{ data.identified || 0 }}</div>
        <div class="live__kpi-lbl">Leads conocidos</div>
      </div>
    </div>

    <div class="live__grid">
      <!-- live visitor feed -->
      <section class="live__card">
        <div class="live__cardhead">
          <span class="live__label">Viendo ahora</span>
          <span v-if="updatedAgo != null" class="live__refresh">actualizado hace {{ updatedAgo }}s</span>
        </div>

        <div v-if="(data.visitors || []).length" class="live__feed">
          <button
            v-for="v in data.visitors"
            :key="v.visitorId || v.path + v.secondsAgo"
            type="button"
            class="live__row"
            :class="{ 'live__row--plain': !v.visitorId, 'live__row--away': v.away }"
            :disabled="!v.visitorId"
            @click="v.visitorId && $refs.journey.open(v.visitorId)"
          >
            <span class="live__rowdot" :class="'live__rowdot--' + (v.leadTier || 'anon')"></span>
            <span class="live__rowmain">
              <span class="live__rowtop">
                <span class="live__rowname">{{ v.identified ? (v.name || "Lead") : "Visitante anónimo" }}</span>
                <span v-if="v.identified && v.leadTier" class="live__lead" :class="'live__lead--' + v.leadTier">
                  {{ tierLabel(v.leadTier) }}<template v-if="v.leadScore != null"> · {{ v.leadScore }}</template>
                </span>
              </span>
              <span class="live__rowwhere">
                <span class="live__pagetag">{{ v.pageLabel || "—" }}</span>
                <span v-if="v.away" class="live__awaytag">En otra pestaña</span>
                <span v-if="v.property" class="live__rowaddr">{{ v.property.fullAddress }}</span>
                <span v-else-if="v.detail" class="live__rowaddr">{{ v.detail }}</span>
              </span>
            </span>
            <span class="live__rowmeta">
              <span class="live__device">{{ deviceLabel(v.device) }}</span>
              <span class="live__ago">{{ agoLabel(v.secondsAgo) }}</span>
              <span
                v-if="v.visitorToken"
                class="live__act live__act--soon"
                aria-disabled="true"
                title="Próximamente"
                @click.stop
              >Conversar</span>
            </span>
          </button>
        </div>
        <div v-else class="live__empty">
          {{ loading ? "Cargando…" : "No hay nadie navegando en este momento." }}
        </div>
      </section>

      <!-- most-watched properties right now -->
      <section class="live__card">
        <div class="live__label">Propiedades más vistas ahora</div>
        <div v-if="(data.topProperties || []).length" class="live__top">
          <div
            v-for="(t, i) in data.topProperties"
            :key="t.property ? t.property.id : i"
            class="live__toprow"
            @click="t.property && $refs.detail.open(t.property)"
          >
            <div class="live__rank">{{ i + 1 }}</div>
            <div class="live__topbody">
              <div class="live__toptype">{{ typeLabel(t.property) }}</div>
              <div class="live__topaddr">{{ t.property ? t.property.fullAddress : "—" }}</div>
            </div>
            <div class="live__topcount">
              <span class="live__rowdot live__rowdot--hot"></span>{{ t.count }}
            </div>
          </div>
        </div>
        <div v-else class="live__empty">{{ loading ? "Cargando…" : "Sin propiedades vistas ahora." }}</div>

        <div v-if="(data.devices || []).length" class="live__devices">
          <span v-for="d in data.devices" :key="d.label" class="live__devchip">
            {{ deviceLabel(d.label) }} · {{ d.count }}
          </span>
        </div>
      </section>
    </div>
    </template>

    <property-detail ref="detail"></property-detail>
    <visitor-journey-dialog
      ref="journey"
      @open-property="$refs.detail.open($event)"
    ></visitor-journey-dialog>
  </div>
</template>

<script>
import { LivePresence } from "@/graphql/queries/analytics";
import { NudgeVisitor } from "@/graphql/queries/chat";
import PropertyDetail from "@/components/properties/Detail";
import VisitorJourneyDialog from "@/components/reports/VisitorJourneyDialog";
import ChatInbox from "@/components/reports/ChatInbox";
import adminOnly from "@/mixins/adminOnly";

const POLL_MS = 10000;

export default {
  mixins: [adminOnly],
  components: { PropertyDetail, VisitorJourneyDialog, ChatInbox },

  data: () => ({
    tierLabels: { hot: "Caliente", warm: "Tibio", cold: "Frío" },
    deviceLabels: { mobile: "Móvil", desktop: "Escritorio", tablet: "Tablet" },
    mode: "traffic", // traffic | chat
    data: {},
    loading: false,
    updatedAt: null,
    updatedAgo: null,
    _poll: null,
    _tick: null
  }),

  created() {
    this.fetch();
    this._poll = setInterval(this.fetch, POLL_MS);
    // a soft "actualizado hace Xs" counter between polls
    this._tick = setInterval(() => {
      if (this.updatedAt) this.updatedAgo = Math.round((Date.now() - this.updatedAt) / 1000);
    }, 1000);
  },

  beforeDestroy() {
    clearInterval(this._poll);
    clearInterval(this._tick);
  },

  methods: {
    fetch() {
      // first load shows a spinner; polls refresh silently
      if (!this.updatedAt) this.loading = true;
      this.$apollo
        .query({ query: LivePresence, fetchPolicy: "no-cache" })
        .then(res => {
          this.data = res.data.livePresence || {};
          this.loading = false;
          this.updatedAt = Date.now();
          this.updatedAgo = 0;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    tierLabel(t) {
      return this.tierLabels[t] || t;
    },
    deviceLabel(d) {
      return this.deviceLabels[d] || "Otro";
    },

    // start (or reopen) a chat with a visitor, then jump to the inbox with it open
    nudge(v) {
      if (!v.visitorToken) return;
      this.$apollo
        .mutate({ mutation: NudgeVisitor, variables: { visitorToken: v.visitorToken } })
        .then(res => {
          const convo = res.data.nudgeVisitor && res.data.nudgeVisitor.conversation;
          if (!convo) return;
          this.mode = "chat";
          this.$nextTick(() => this.$refs.inbox && this.$refs.inbox.open(convo));
        })
        .catch(() => {});
    },
    typeLabel(property) {
      if (!property || !property.propertyType) return "Propiedad";
      return this.$t("property.type." + property.propertyType.toLowerCase());
    },
    agoLabel(secs) {
      const s = Number(secs) || 0;
      if (s < 5) return "ahora";
      if (s < 60) return `hace ${s}s`;
      return `hace ${Math.floor(s / 60)}m`;
    }
  }
};
</script>

<style scoped>
.live { background: #f7f5f1; min-height: 100%; overflow-x: hidden; }

.live__masthead { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
.live__mastheadtext { min-width: 0; }
.live__eyebrow {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 10px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase;
  color: #b52118; margin-bottom: 7px;
}
.live__dot {
  width: 8px; height: 8px; border-radius: 999px; background: #c2bcb0; flex: none;
}
.live__dot--on {
  background: #b52118;
  box-shadow: 0 0 0 0 rgba(181, 33, 24, .5); animation: live-pulse 1.8s infinite;
}
@keyframes live-pulse {
  0% { box-shadow: 0 0 0 0 rgba(181, 33, 24, .45); }
  70% { box-shadow: 0 0 0 7px rgba(181, 33, 24, 0); }
  100% { box-shadow: 0 0 0 0 rgba(181, 33, 24, 0); }
}
@media (prefers-reduced-motion: reduce) { .live__dot--on { animation: none; } }
.live__title { font-size: 28px; font-weight: 700; line-height: 1.05; color: #1a1814; margin: 0; }
.live__sub { font-size: 13.5px; color: #756f64; margin: 5px 0 0; }

/* traffic / chat toggle — compact, top-right of the masthead */
.live__modes { display: inline-flex; background: #f1ece3; border-radius: 9px; padding: 3px; gap: 2px; flex: none; }
.live__mode { border: none; background: transparent; cursor: pointer; font: inherit; font-size: 13px; font-weight: 600; color: #6a645a; border-radius: 7px; padding: 7px 18px; transition: background 0.15s, color 0.15s; }
.live__mode--on { background: #fff; color: #1a1814; font-weight: 700; box-shadow: 0 1px 4px -1px rgba(40, 33, 22, 0.3); }
.live__mode:focus-visible { outline: 2px solid #b52118; outline-offset: 2px; }

/* "Conversar" action on a feed row */
.live__act { flex: none; font-size: 11.5px; font-weight: 700; color: #b52118; border: 1px solid #f1c9c5; background: #fffaf9; border-radius: 7px; padding: 4px 10px; cursor: pointer; transition: background 0.15s, border-color 0.15s; }
.live__act:hover { background: #fdecea; border-color: #b52118; }
.live__act:focus-visible { outline: 2px solid #b52118; outline-offset: 2px; }
/* "upcoming" — disabled placeholder, non-interactive */
.live__act--soon { color: #a39a8c; border-color: #ece7de; background: #f6f2ea; cursor: not-allowed; }
.live__act--soon:hover { background: #f6f2ea; border-color: #ece7de; }

.live__kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 18px; }
@media (max-width: 720px) { .live__kpis { grid-template-columns: 1fr; } }
.live__kpi {
  background: #fff; border: 1px solid #ece7de; border-radius: 14px; padding: 18px 20px;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
}
.live__kpi--hero { background: #1a1814; border-color: #1a1814; }
.live__kpi--hero .live__kpi-num { color: #fff; }
.live__kpi--hero .live__kpi-lbl { color: #c8c0b3; }
.live__kpi-num { font-size: 30px; font-weight: 700; color: #1a1814; line-height: 1; }
.live__kpi-lbl { font-size: 13px; color: #756f64; margin-top: 6px; }
.live__kpi-extra { font-size: 12px; font-weight: 600; color: #c8c0b3; margin-top: 8px; }

.live__grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 16px; align-items: start; }
@media (max-width: 860px) { .live__grid { grid-template-columns: 1fr; } }

.live__card {
  background: #fff; border: 1px solid #ece7de; border-radius: 14px; padding: 18px 20px;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18); margin-bottom: 16px; min-width: 0;
}
.live__cardhead { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.live__label {
  font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: #a39a8c;
  font-weight: 600; margin-bottom: 10px;
}
.live__refresh { font-size: 11px; color: #b8b0a2; }

/* visitor feed */
.live__feed { display: flex; flex-direction: column; gap: 8px; }
.live__row {
  display: flex; align-items: flex-start; gap: 12px; width: 100%; text-align: left;
  background: #fff; border: 1px solid #ece7de; border-radius: 12px; padding: 12px 14px;
  cursor: pointer; font: inherit; color: inherit;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.live__row:hover { border-color: #dcd4c6; box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18); transform: translateY(-1px); }
.live__row--plain { cursor: default; }
.live__row--plain:hover { border-color: #ece7de; box-shadow: none; transform: none; }
/* backgrounded ("away") tab — present but not actively looking; de-emphasized */
.live__row--away { background: #faf8f4; opacity: 0.72; }
.live__row--away .live__rowdot { background: #c2bcb0 !important; }
.live__rowdot { flex: none; width: 9px; height: 9px; border-radius: 50%; margin-top: 5px; background: #a39a8c; }
.live__rowdot--hot { background: #b52118; }
.live__rowdot--warm { background: #d8941f; }
.live__rowdot--cold { background: #5a6b7a; }
.live__rowdot--anon { background: #c2bcb0; }
.live__rowmain { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.live__rowtop { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.live__rowname { font-size: 14px; font-weight: 600; color: #1a1814; }
.live__lead { flex: none; font-size: 9.5px; font-weight: 700; padding: 1px 7px; border-radius: 999px; text-transform: uppercase; letter-spacing: .03em; }
.live__lead--hot { background: #fdecea; color: #b52118; }
.live__lead--warm { background: #fdeecf; color: #8a5a00; }
.live__lead--cold { background: #eef1f4; color: #6a727b; }
.live__rowwhere { display: flex; align-items: center; gap: 8px; min-width: 0; }
.live__pagetag {
  flex: none; font-size: 11px; font-weight: 600; color: #56504a;
  background: #f1ece3; border-radius: 999px; padding: 1px 8px;
}
.live__awaytag {
  flex: none; font-size: 10px; font-weight: 700; letter-spacing: .02em; text-transform: uppercase;
  color: #8a8377; background: #ece9e3; border-radius: 999px; padding: 1px 8px;
}
.live__rowaddr { font-size: 12.5px; color: #756f64; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.live__rowmeta { flex: none; display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
.live__device { font-size: 11.5px; color: #756f64; }
.live__ago { font-size: 11px; color: #a39a8c; }

/* top properties */
.live__top { display: flex; flex-direction: column; gap: 8px; }
.live__toprow {
  display: flex; align-items: center; gap: 12px; padding: 11px 13px; cursor: pointer;
  border: 1px solid #ece7de; border-radius: 12px;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.live__toprow:hover { border-color: #dcd4c6; box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18); transform: translateY(-1px); }
.live__rank {
  flex: none; width: 24px; height: 24px; border-radius: 50%; background: #faf8f4; color: #756f64;
  display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700;
}
.live__topbody { flex: 1 1 auto; min-width: 0; }
.live__toptype { font-size: 13.5px; font-weight: 600; color: #1a1814; }
.live__topaddr { font-size: 12px; color: #756f64; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.live__topcount { flex: none; display: inline-flex; align-items: center; gap: 6px; font-size: 16px; font-weight: 700; color: #b52118; }
.live__topcount .live__rowdot { margin-top: 0; }

.live__devices { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 14px; padding-top: 14px; border-top: 1px solid #f1ece3; }
.live__devchip { font-size: 11.5px; font-weight: 600; color: #56504a; background: #f4f0e8; border-radius: 999px; padding: 3px 10px; }

.live__empty {
  border: 1px dashed #dcd4c6; border-radius: 12px; padding: 32px; text-align: center;
  color: #a39a8c; font-size: 13.5px;
}
</style>
